/* oxlint-disable react-doctor/rendering-hydration-mismatch-time */
import { set } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { errorAlert, successAlert } from 'utils/alert';

import { useCreateRoute } from 'components/create-dialog/CreateDialogContext';
import {
    MultiSelectChangeDetails,
    MultiSelectChangeReason,
} from 'components/forms/inputs/rhf-inputs/DynamicMultiSelectInput';
import { isEmail } from 'components/forms/inputs/EmailListInput';
import { isValidUrl } from 'components/forms/inputs/rhf-validators/validators.ts';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import { useModal } from 'components/modals/useModal';
import { activeCasesFilter } from 'components/tables/list-table/filter-pieces/filterConstants';
import { useCancelTaskCalendarEventMutation, useLazyGetOrganizerEmailQuery } from 'store/api/tasks';
import { ClassifierData } from 'types/classifiers';
import { ContactMinDto } from 'types/contacts.ts';
import { EmployeeListItemMinDto } from 'types/employees.ts';
import { MatterListItemMin } from 'types/matters.ts';
import { MinStringOption, NamedEntityMinDto } from 'types/other';
import { TaskForm, TaskMatterForm, TaskTypeCode } from 'types/tasks';
import { formatDateStringInput } from 'utils/formatDateStringInput';
import { getOptionsFromClassifier, getValidOptionsFromClassifier } from 'utils/formUtil';
import { t } from 'utils/getTranslations';
import { logError } from 'utils/monitoring';

export interface TaskFormProps {
    type: 'add' | 'edit';
    initialData?: TaskForm;
    classifiers: Record<string, ClassifierData>;
}

const REMINDER_ORDER = [
    'PT0S',
    '-PT5M',
    '-PT10M',
    '-PT15M',
    '-PT30M',
    '-PT1H',
    '-PT2H',
    '-PT3H',
    '-PT4H',
    '-PT6H',
    '-PT12H',
    '-P1D',
    '-P2D',
    '-P3D',
    '-P7D',
    '-P14D',
];

const DEADLINE_TASK_TYPES = [
    TaskTypeCode.CASE_DEADLINE,
    TaskTypeCode.COURT_HEARING,
    TaskTypeCode.FILING_DEADLINE,
    TaskTypeCode.PROCEDURAL_DEADLINE,
];

/** The task form's values, the handlers that keep its fields in step, and what its field sections read. */
export const useTaskForm = ({ type = 'add', initialData, classifiers }: TaskFormProps) => {
    const { id: taskId } = useCreateRoute().params;
    const [eventDateValidationError, setIsEventDateValidationError] = useState(false);
    const [eventTimeValidationError, setIsEventTimeValidationError] = useState(false);

    const methods = useForm<TaskForm>({
        defaultValues: initialData,
        values: initialData,
    });
    const { control, setValue, getValues } = methods;
    const [cancelTaskCalendarEvent] = useCancelTaskCalendarEventMutation();
    const [getOrganizerEmail] = useLazyGetOrganizerEmailQuery();
    const { openModal, closeModal } = useModal();

    const [
        clients,
        taskTypes,
        matters,
        calendarSync,
        eventDateStart,
        eventDateEnd,
        timeStart,
        timeEnd,
        deadlineDate,
        status,
        eventInvitees,
        replyTo,
        isOfficial,
        hasDeadline,
        allDayMeeting,
        eventDescription,
        correspondence,
    ] = useWatch({
        control,
        name: [
            'clientParties',
            'taskTypes',
            'matters',
            'calendarSync',
            'taskEvent.dateStart',
            'taskEvent.dateEnd',
            'taskEvent.timeStart',
            'taskEvent.timeEnd',
            'deadlineDate',
            'taskStatusTypeCode',
            'taskEvent.eventInvitees',
            'replyTo',
            'isOfficial',
            'hasDeadline',
            'taskEvent.allDayMeeting',
            'taskEvent.description',
            'correspondence',
        ],
    });

    const prefillFormFieldsConnectedWithMatter = useCallback(
        (matters: TaskMatterForm[] | MatterListItemMin[] | null) => {
            if (!matters || !matters.length) {
                return;
            }

            const currentDescription = getValues('description') || '';
            const newDescriptions = matters
                .filter((matter) => matter.description && !currentDescription.includes(matter.description))
                .map((matter) => matter.description);

            if (newDescriptions.length > 0) {
                const updatedDescription = currentDescription
                    ? `${currentDescription}, ${newDescriptions.join(', ')}`
                    : newDescriptions.join(', ');
                setValue('description', updatedDescription);
            }

            // Skip employee auto-population when task is connected to correspondence
            const connectedCorrespondence = getValues('correspondence');
            if (connectedCorrespondence && connectedCorrespondence.length > 0) {
                return;
            }

            const currentAssignedEmployees = getValues('assignedToEmployees') || [];
            const allStaffEmployees = matters
                .flatMap((matter) => matter.employees || [])
                .filter((emp) => emp.matterEmployeeRoleTypeCode === 'STAFF')
                .map((emp) => emp.employee);

            const responsibleEmployees = matters
                .map((matter) => matter.responsibleEmployee)
                .filter((emp): emp is NamedEntityMinDto => emp != null);

            const supervisedEmployees = matters
                .map((matter) => matter.supervisedByEmployee)
                .filter((emp): emp is NamedEntityMinDto => emp != null);

            const combined = [
                ...currentAssignedEmployees,
                ...allStaffEmployees,
                ...responsibleEmployees,
                ...supervisedEmployees,
            ];

            const uniqueEmployees = combined.filter(
                (emp, index, self) => index === self.findIndex((e) => e.id === emp.id)
            );

            if (uniqueEmployees.length !== currentAssignedEmployees.length) {
                setValue('assignedToEmployees', uniqueEmployees);
            }
        },
        [getValues, setValue]
    );

    const prefillFormFieldsConnectedWithContact = useCallback(
        (contacts: NamedEntityMinDto[] | null) => {
            if (!contacts || !contacts.length) {
                return;
            }

            // Handle contact name in name field
            const currentName = getValues('name') || '';
            const newName = contacts
                .filter((contact) => contact.name && !currentName.includes(contact.name))
                .map((contact) => contact.name);

            if (newName.length > 0) {
                const updatedName = currentName ? `${currentName}, ${newName.join(', ')}` : newName.join(', ');
                setValue('name', updatedName);
            }
        },
        [getValues, setValue]
    );

    // Resolve the read-only "Meeting organizer" field from the backend.
    // The organizer depends on the task types AND on the official + deadline flags
    // (official tasks with a deadline use a tenant-specific from-address), so it must be
    // refreshed whenever any of those change.
    // - In add mode: always refresh
    // - In edit mode: only refresh if the event was not sent yet (no eventId)
    const refreshOrganizerEmail = useCallback(
        (taskTypeCodes: string[], official: boolean, deadline: boolean) => {
            const eventAlreadySent = initialData?.taskEvent?.eventId != null;
            if (type !== 'add' && eventAlreadySent) {
                return;
            }
            getOrganizerEmail({ taskTypeCodes, isOfficial: official, hasDeadline: deadline }).then((result) => {
                if (result.data) {
                    setValue('taskEvent.inviteSender', result.data.organizerEmail);
                }
            });
        },
        [type, initialData?.taskEvent?.eventId, getOrganizerEmail, setValue]
    );

    const handleOnTaskTypesChange = useCallback(
        (types: MinStringOption[] | null) => {
            if (!types || !types.length) {
                return;
            }

            const isDeadlineTaskType = types.some((el) => DEADLINE_TASK_TYPES.includes(el.value as TaskTypeCode));
            if (type === 'add' && isDeadlineTaskType) {
                const highPriorityType = (
                    getOptionsFromClassifier(classifiers['TASK_PRIORITY_TYPE']) as {
                        label: string;
                        value: string;
                    }[]
                ).find((val) => val.value === 'HIGH');
                setValue('taskPriority', highPriorityType);

                prefillFormFieldsConnectedWithMatter(getValues('matters'));
                prefillFormFieldsConnectedWithContact(getValues('clientParties'));
            }

            refreshOrganizerEmail(
                types.map((t) => t.value),
                getValues('isOfficial') ?? false,
                getValues('hasDeadline') ?? false
            );
        },
        [
            type,
            getValues,
            prefillFormFieldsConnectedWithMatter,
            prefillFormFieldsConnectedWithContact,
            classifiers,
            setValue,
            refreshOrganizerEmail,
        ]
    );

    const validUrl = useCallback((val: unknown) => isValidUrl(val, t('validation.dms-link-must-be-valid')), []);

    const handleHasDeadlineToggle = useCallback(
        (value: boolean) => {
            if (value) {
                if (!deadlineDate) {
                    setValue('deadlineDate', formatDateStringInput(new Date()));
                }
            } else {
                setValue('deadlineDate', null);
            }
            refreshOrganizerEmail(
                (getValues('taskTypes') ?? []).map((t) => t.value),
                getValues('isOfficial') ?? false,
                value
            );
        },
        [deadlineDate, setValue, refreshOrganizerEmail, getValues]
    );

    const handleIsOfficialToggle = useCallback(
        (value: boolean) => {
            refreshOrganizerEmail(
                (getValues('taskTypes') ?? []).map((t) => t.value),
                value,
                getValues('hasDeadline') ?? false
            );
        },
        [refreshOrganizerEmail, getValues]
    );

    const handleAllDayMeetingToggle = useCallback(
        (value: boolean) => {
            if (value && timeStart && timeEnd) {
                setValue('taskEvent.timeStart', set(timeStart, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 }), {
                    shouldDirty: true,
                });
                setValue('taskEvent.timeEnd', set(timeEnd, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 }), {
                    shouldDirty: true,
                });
                if (eventDateStart) {
                    setValue('taskEvent.dateEnd', eventDateStart, {
                        shouldDirty: true,
                    });
                }
            }
        },
        [timeStart, timeEnd, eventDateStart, setValue]
    );

    const handleCancelCalendarInvite = useCallback(() => {
        openModal(
            <ConfirmationModal
                titleId="confirmation"
                content={<p className="text-body1">{t('tasks.alerts.cancel-calendar-invite-warning')}</p>}
                confirmId="cancel-invite-modal.yes"
                cancelId="cancel-invite-modal.no"
                onConfirm={() => {
                    void (async () => {
                        try {
                            if (!taskId) {
                                errorAlert(t('tasks.alerts.error-cancelling-calendar-invite'));
                                closeModal();
                                return;
                            }

                            // Call dedicated DELETE endpoint for calendar event cancellation
                            await cancelTaskCalendarEvent({ id: taskId }).unwrap();

                            // Update form state to reflect cancellation
                            setValue('calendarSync', false);
                            setValue('taskEvent', null);

                            closeModal();
                            successAlert(t('tasks.alerts.calendar-invite-cancelled'));
                        } catch (error) {
                            console.error('Failed to cancel calendar invite:', error);
                            logError(error);
                            errorAlert(t('tasks.alerts.error-cancelling-calendar-invite'));
                        }
                    })();
                }}
                onCancel={closeModal}
            />
        );
    }, [openModal, closeModal, taskId, cancelTaskCalendarEvent, setValue]);

    // TODO: cascading-set-state skip — RHF `setValue` framework call plus validation flag updates from coalesced watched form fields; refactor would change validation strategy.
    useEffect(() => {
        const todayDate = new Date();
        todayDate.setHours(0);
        todayDate.setMinutes(0);
        todayDate.setSeconds(0);

        if (eventDateStart && eventDateEnd) {
            if (eventDateEnd < eventDateStart) {
                setValue('taskEvent.dateEnd', eventDateStart, {
                    shouldDirty: true,
                });
            } else {
                // oxlint-disable-next-line react-hooks-js/set-state-in-effect -- validation flag, see the TODO above
                setIsEventDateValidationError(false);
            }
        }
        if (timeEnd && timeStart && eventDateStart && eventDateEnd) {
            if (timeEnd < timeStart && eventDateStart === eventDateEnd) {
                // oxlint-disable-next-line react-hooks-js/set-state-in-effect -- validation flag, see the TODO above
                setIsEventTimeValidationError(true);
            } else {
                // oxlint-disable-next-line react-hooks-js/set-state-in-effect -- validation flag, see the TODO above
                setIsEventTimeValidationError(false);
            }
        }
    }, [eventDateStart, eventDateEnd, timeStart, timeEnd, setValue, allDayMeeting]);

    useEffect(() => {
        if (!eventDescription) {
            const taskTypesString = taskTypes?.map((taskType) => taskType.label).join(', ');
            const clientsName = clients?.map((client) => client.name).join(', ');
            const mattersString = matters
                ?.map((matter) => (matter.caseNumber ? matter.caseNumber + ' ' + matter.name : matter.name))
                .join(', ');

            const firstPart = [taskTypesString, clientsName, mattersString].filter((value) => !!value).join(', ');

            const formattedDesc = firstPart.replace(/\\n/g, '\n');
            if (formattedDesc !== (eventDescription ?? '')) {
                setValue('taskEvent.description', formattedDesc);
            }
        }
    }, [taskTypes, clients, matters, setValue, eventDescription]);

    const handleOnContactsChange = useCallback(
        (contacts: ContactMinDto[] | null) => {
            const isDeadlineTaskType = (taskTypes || []).some((el) =>
                DEADLINE_TASK_TYPES.includes(el.value as TaskTypeCode)
            );
            if (type === 'add' && isDeadlineTaskType) {
                prefillFormFieldsConnectedWithContact(contacts);
            }

            const contactIds = contacts?.map((client) => client.id) || [];
            if (!matters || matters.length === 0) {
                return;
            }
            if (contactIds.length === 0) {
                setValue('matters', []);
                return;
            }

            const validMatters = matters.filter((matter) => contactIds.includes(matter.clientParty.id));
            setValue('matters', validMatters);
        },
        [taskTypes, type, matters, setValue, prefillFormFieldsConnectedWithContact]
    );

    const handleOnMattersChange = useCallback(
        (matters: MatterListItemMin[] | null) => {
            const isDeadlineTaskType = (taskTypes || []).some((el) =>
                DEADLINE_TASK_TYPES.includes(el.value as TaskTypeCode)
            );
            if (type === 'add' && isDeadlineTaskType) {
                prefillFormFieldsConnectedWithMatter(matters);
            }

            // Add clientParties from selected matters if not already present
            if (matters && matters.length > 0) {
                const currentClientParties = getValues('clientParties') || [];
                const clientPartyIds = new Set(currentClientParties.map((cp) => cp.id));

                const newClientParties = matters
                    .map((matter) => matter.clientParty)
                    .filter((clientParty) => !clientPartyIds.has(clientParty.id))
                    .map((clientParty) => ({ id: clientParty.id, name: clientParty.name }));

                if (newClientParties.length > 0) {
                    setValue('clientParties', [...currentClientParties, ...newClientParties]);
                }
            }
        },
        [taskTypes, type, prefillFormFieldsConnectedWithMatter, getValues, setValue]
    );

    const mattersClientsFilter = useMemo(() => {
        return [
            clients && clients.length > 0 ? `clientParty.id=in=(${clients.map((client) => client.id)})` : '',
            activeCasesFilter,
        ]
            .filter(Boolean)
            .join(';');
    }, [clients]);

    const selectedMatterIds = useMemo(() => matters?.map((m) => m.id).join(','), [matters]);

    const reminderOptions = (
        getValidOptionsFromClassifier(classifiers['EVENT_REMINDER_CODE']) as { label: string; value: string }[]
    ).sort((a, b) => REMINDER_ORDER.indexOf(a.value) - REMINDER_ORDER.indexOf(b.value));

    const updateEventInvitees = useCallback(
        (
            _: NamedEntityMinDto[] | null,
            reason: MultiSelectChangeReason | undefined,
            details: MultiSelectChangeDetails<EmployeeListItemMinDto> | undefined
        ) => {
            if (!calendarSync) {
                return;
            }
            const detailsEmployee: NamedEntityMinDto | undefined = details
                ? { id: details?.option.id, name: details?.option.name }
                : undefined;
            let updatedEventInvitees = eventInvitees ?? [];

            if (detailsEmployee) {
                switch (reason) {
                    case 'selectOption':
                        if (!updatedEventInvitees.find((e) => e.id === detailsEmployee.id)) {
                            updatedEventInvitees.push(detailsEmployee);
                        }
                        break;
                    case 'removeOption':
                        updatedEventInvitees = updatedEventInvitees.filter(
                            (employee) => employee.id !== detailsEmployee.id
                        );
                        break;
                }
            }
            setValue('taskEvent.eventInvitees', updatedEventInvitees);
        },
        [calendarSync, eventInvitees, setValue]
    );

    const removeEmail = (val: string, field: 'replyTo') => {
        const toState = getValues(field)?.filter((va) => va !== val);
        setValue(field, toState, { shouldDirty: true, shouldValidate: true });
    };

    const addEmail = (val: string, field: 'replyTo') => {
        const toState = getValues(field);
        if (isEmail(val)) toState?.push(val);
        setValue(field, toState, { shouldDirty: true, shouldValidate: true });
    };

    useEffect(() => {
        const assignedToEmployees = getValues('assignedToEmployees')?.map(({ id, name }) => ({ id, name }));
        if (calendarSync && getValues('taskEvent.eventInvitees')?.length === 0 && assignedToEmployees?.length) {
            setValue('taskEvent.eventInvitees', assignedToEmployees);
        }
    }, [calendarSync, getValues, setValue]);

    useEffect(() => {
        const currentAssignedEmployees = getValues('assignedToEmployees');
        const taskTypes = getValues('taskTypes');
        const isDeadlineTaskType = (taskTypes || []).some((el) =>
            DEADLINE_TASK_TYPES.includes(el.value as TaskTypeCode)
        );

        // Skip employee auto-population when task is connected to correspondence
        const connectedCorrespondence = getValues('correspondence');
        const hasCorrespondence = connectedCorrespondence && connectedCorrespondence.length > 0;

        if (
            isDeadlineTaskType &&
            matters &&
            matters.length > 0 &&
            (!currentAssignedEmployees || currentAssignedEmployees.length === 0) &&
            !hasCorrespondence
        ) {
            const staffEmployees = matters
                .flatMap((matter) => matter.employees || [])
                .filter((emp) => emp.matterEmployeeRoleTypeCode === 'STAFF')
                .map((emp) => emp.employee);

            // Collect responsible and supervised employees from matters
            const responsibleEmployees = matters
                .map((matter) => matter.responsibleEmployee)
                .filter((emp): emp is NamedEntityMinDto => emp != null);

            const supervisedEmployees = matters
                .map((matter) => matter.supervisedByEmployee)
                .filter((emp): emp is NamedEntityMinDto => emp != null);

            // Merge all employees
            const combined = [...staffEmployees, ...responsibleEmployees, ...supervisedEmployees];

            // Remove duplicates based on employee id
            const uniqueEmployees = combined.filter(
                (emp, index, self) => index === self.findIndex((e) => e.id === emp.id)
            );

            if (uniqueEmployees.length > 0) {
                setValue('assignedToEmployees', uniqueEmployees);
            }
        }
    }, [matters, correspondence, getValues, setValue]);

    return {
        addEmail,
        allDayMeeting,
        calendarSync,
        classifiers,
        eventDateValidationError,
        eventTimeValidationError,
        handleAllDayMeetingToggle,
        handleCancelCalendarInvite,
        handleHasDeadlineToggle,
        handleIsOfficialToggle,
        handleOnContactsChange,
        handleOnMattersChange,
        handleOnTaskTypesChange,
        hasDeadline,
        initialData,
        isOfficial,
        matters,
        mattersClientsFilter,
        methods,
        reminderOptions,
        removeEmail,
        replyTo,
        selectedMatterIds,
        status,
        taskId,
        type,
        updateEventInvitees,
        validUrl,
    };
};

export type TaskFormState = ReturnType<typeof useTaskForm>;

/** The event's date or time error the form shows, which blocks a save. */
export const eventValidationErrorId = ({
    eventDateValidationError,
    eventTimeValidationError,
}: TaskFormState): string | null => {
    if (eventDateValidationError) {
        return 'tasks.form.date-error';
    }
    return eventTimeValidationError ? 'tasks.form.time-error' : null;
};
